// WS5 PR3.1 — Viewbox state-machine (reducer puro).
//
// Cfr. types.ts per ViewboxState/Event. Pattern reducer puro speculare di
// `walk/state-machine.ts`: ritorna stesso reference su no-op per consentire
// dispatch a short-circuit equality.

import {
	arrayIdxFromNumericIndex,
	INITIAL_VIEWBOX_STATE,
	type ViewboxEvent,
	type ViewboxSnapshot,
	type ViewboxState,
} from "./types.js";

export function viewboxTransition(
	state: ViewboxState,
	event: ViewboxEvent,
): ViewboxState {
	switch (event.type) {
		case "LOAD": {
			// Reset completo a load nuovo manifest. Default array-idx derivato:
			//  - se defaultIdx fornito + matchato → usa quel array-idx.
			//  - se defaultIdx null o no match → primo (0) se viewboxes presenti.
			//  - se viewboxes vuote → null + firstPersonAvailable=false (legacy).
			if (event.viewboxes.length === 0) {
				return {
					available: event.viewboxes,
					currentArrayIdx: null,
					teleportPhase: "idle",
					firstPersonAvailable: false,
				};
			}
			let arrayIdx: number | null = null;
			if (event.defaultIdx !== null) {
				arrayIdx = arrayIdxFromNumericIndex(event.viewboxes, event.defaultIdx);
			}
			if (arrayIdx === null) arrayIdx = 0;
			return {
				available: event.viewboxes,
				currentArrayIdx: arrayIdx,
				teleportPhase: "idle",
				firstPersonAvailable: true,
			};
		}

		case "SELECT": {
			// Spostamento istantaneo (no glide): no-op se idx invalido o gia' attivo.
			// Codex PR3.1 W1: `Number.isInteger` rifiuta NaN/Infinity/float
			// (es. malformed dispatch dal layer UI) prima del bounds check.
			if (
				state.teleportPhase !== "idle" ||
				!Number.isInteger(event.arrayIdx) ||
				event.arrayIdx < 0 ||
				event.arrayIdx >= state.available.length ||
				event.arrayIdx === state.currentArrayIdx
			) {
				return state;
			}
			return { ...state, currentArrayIdx: event.arrayIdx };
		}

		case "TELEPORT_START": {
			// Inizio glide animato: blocca walk/click-to-move lato orchestrator
			// finche' TELEPORT_COMPLETE/ABORT. No-op se idx invalido o gia'
			// in glide. Codex PR3.1 W1: integer guard come SELECT.
			// Codex PR3.1 W3 (note PR3.3): durante teleportPhase='gliding' la
			// UI switcher (PR3.3) DEVE disabilitare i button per evitare che
			// l'utente percepisca i no-op qui come click persi.
			if (
				state.teleportPhase !== "idle" ||
				!Number.isInteger(event.arrayIdx) ||
				event.arrayIdx < 0 ||
				event.arrayIdx >= state.available.length ||
				event.arrayIdx === state.currentArrayIdx
			) {
				return state;
			}
			return {
				...state,
				currentArrayIdx: event.arrayIdx,
				teleportPhase: "gliding",
			};
		}

		case "TELEPORT_COMPLETE": {
			// Fine glide: rilascia walk/click-to-move. currentArrayIdx era gia'
			// stato settato dal TELEPORT_START → solo phase change.
			if (state.teleportPhase !== "gliding") return state;
			return { ...state, teleportPhase: "idle" };
		}

		case "TELEPORT_ABORT": {
			// Cancellazione glide (es. utente preme Esc o cambia modalita'):
			// rilascia phase, mantiene currentArrayIdx (camera potrebbe essere
			// a meta' glide → orchestrator deve gestire snap/cleanup).
			if (state.teleportPhase !== "gliding") return state;
			return { ...state, teleportPhase: "idle" };
		}

		case "RESET":
			return INITIAL_VIEWBOX_STATE;
	}
}

/** Snapshot stabile (struttura piatta) per UI components / event emitter.
 *  Stessa reference su no-change non garantita (snapshot e' sempre un
 *  nuovo dict): l'equality lato orchestrator usa il pattern oldState ===
 *  newState dei reducer puri sopra. */
export function viewboxSnapshot(state: ViewboxState): ViewboxSnapshot {
	return {
		available: state.available,
		currentArrayIdx: state.currentArrayIdx,
		teleportPhase: state.teleportPhase,
		firstPersonAvailable: state.firstPersonAvailable,
	};
}
